import { getDropPhase, toDropRawStatus } from "./permissions";
import type { Drop, DropItem, DropItemsPage, DropSummary } from "./types";
import { formatTokenSymbol } from "@/lib/asset-naming";

/**
 * Reads the drop's own database id out of a raw drop payload.
 *
 * `drop-item/create` validates `drop_id` as `exists:drops,id`, so it needs the
 * real numeric id. Both `/drop` and `/drop/{slug}` now return it as `id` (the
 * payloads previously carried no id at all, which is why this used to scrape
 * `drop_id` off the drop's own items — that indirection is gone).
 *
 * Only `raw.id` is read: `user_id` and `collection_id` also sit on this payload
 * and are different entities, so nothing else here is a valid substitute.
 *
 * Returns undefined when the field is missing or unusable, which callers must
 * treat as "id unknown" rather than "drop doesn't exist".
 */
export function resolveDropNumericId(raw: any): number | undefined {
  const numeric = Number(raw?.id);
  if (raw?.id == null || raw.id === "" || !Number.isFinite(numeric) || numeric <= 0) {
    return undefined;
  }
  return numeric;
}

/** The parent drop's network is the authoritative currency scope for a new
 * item. A missing id must remain missing — currency queries default to
 * Ethereum when called without one, which would offer invalid payment assets. */
export function resolveDropNetworkId(raw: any): number | undefined {
  // `/drop/{slug}` returns the network in more than one established shape:
  // `network_id: 3`, `network: { id: 3 }`, and (notably) `network: 3`.
  // Treating the last one as an object was why a Matic drop disabled its
  // currency control and then fell back to Ethereum elsewhere.
  const network = raw?.network;
  const candidate = raw?.network_id ?? (typeof network === "object" && network !== null ? network.id : network);
  const numeric = Number(candidate);
  if (candidate == null || candidate === "" || !Number.isFinite(numeric) || numeric <= 0) {
    return undefined;
  }
  return numeric;
}

/**
 * Pulls the array out of a `/drop-item` response. The endpoint returns it
 * under `data.item` — singular — which the previous readers (`data.items` /
 * `data.dropItems`) never matched, so every drop-item lookup silently came
 * back empty.
 */
const finite = (v: unknown): number | null => {
  const n = typeof v === "string" && v.trim() ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

/** The embedded items' page — null when the payload carries none (a bare array, or no items). */
export function resolveItemsPage(page: any): DropItemsPage | null {
  const current = finite(page?.current_page);
  const last = finite(page?.last_page);
  const perPage = finite(page?.per_page);
  if (current === null || last === null || perPage === null || perPage <= 0) return null;
  return { current, last, perPage, total: finite(page?.total) };
}

/**
 * The drop's own totals over ALL its items (`summary`) — what the page used
 * to add up from the items, which it can no longer do now that they arrive a
 * page at a time. Null when absent, so the callers fall back to the items.
 */
export function resolveDropSummary(summary: any): DropSummary | null {
  if (!summary || typeof summary !== "object") return null;
  const quantity = finite(summary.quantity);
  const available = finite(summary.available);
  if (quantity === null || available === null || quantity < 0 || available < 0) return null;
  return {
    quantity,
    available: Math.min(available, quantity),
    priceMin: finite(summary.price_min),
    priceMax: finite(summary.price_max),
    currencySymbol: formatTokenSymbol(summary.currency?.symbol) || null,
  };
}

export function extractDropItems(response: any): any[] {
  const data = response?.data;
  const list = data?.item ?? data?.items ?? data?.dropItems ?? data;
  return Array.isArray(list) ? list : [];
}

/** Price labels must use the item's currency record, never the parent drop's
 * network/summary symbol. Do not invent a token when the API omits it. */
export function dropItemCurrencySymbol(item: Pick<DropItem, "currency">): string {
  return formatTokenSymbol(item.currency?.symbol);
}

export function sanitizeDropUrl(url: string | undefined | null, fallback: string = "/sampleNft1.png"): string {
  if (!url) return fallback;
  let cleanUrl = url.replace(/[`'"]/g, "").trim();
  if (!cleanUrl) return fallback;
  if (cleanUrl.startsWith("ipfs://")) {
    return cleanUrl.replace("ipfs://", "https://ipfs.io/ipfs/");
  }
  try {
    new URL(cleanUrl);
    return cleanUrl;
  } catch {
    if (cleanUrl.startsWith("/")) return cleanUrl;
    return `/${cleanUrl}`;
  }
}

/**
 * The drop's minted count, or null when the payload states none.
 *
 * Only the four fields the API has ever been seen to use are read, in order.
 * A field that is present but unusable (empty string, non-numeric, negative)
 * is treated as absent rather than coerced — `Number("")` is 0, and that
 * silent 0 is exactly the fabricated scarcity figure F97 is about.
 */
export function resolveMintedCount(raw: any): number | null {
  for (const candidate of [raw?.mintedCount, raw?.minted_count, raw?.minted, raw?.sold]) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const numeric = Number(candidate);
    if (!Number.isFinite(numeric) || numeric < 0) continue;
    return numeric;
  }
  return null;
}

/**
 * The drop's summary mint price, or null when the payload states none.
 *
 * Same shape as `resolveMintedCount`, for the same reason: `?? 0` used to
 * collapse a missing price onto a real `0`, and every consumer (`DropCard`,
 * `DropsHeroSlider`, the drop detail page) renders `price === 0` as
 * "Free"/"رایگان" — so a payload that simply omitted price displayed as a
 * genuinely free drop. Only the per-item mint price (`DropItemCard`,
 * `review-rows.ts`) was ever fixed for this; this is the summary path.
 */
export function resolveDropPrice(raw: any): number | null {
  for (const candidate of [raw?.price, raw?.mint_price, raw?.unit_price]) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const numeric = Number(candidate);
    if (!Number.isFinite(numeric) || numeric < 0) continue;
    return numeric;
  }
  return null;
}

/**
 * Reads a datetime field off a raw drop payload, trying the names the API has
 * been seen to use. Returns null when none is present or usable — callers treat
 * that as "unknown", and `getMintability` blocks on an unknown start time
 * rather than assuming the drop has started.
 */
function readDateField(raw: any, keys: string[]): string | null {
  for (const key of keys) {
    const value = raw?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

/**
 * The drop's required start time.
 *
 * `start_time` leads: it is the name the `/drop/{slug}` payload carries the
 * mint start under (`published_at` beside it is when the drop was published —
 * a different, earlier date). `publish_at` follows because it is the only date
 * key this repo has ever sent to `/drop/create` (commit def42b0). The rest stay
 * as read tolerance for payload shapes this client has seen elsewhere, but none
 * of them is the contract, and nothing is ever WRITTEN under those names.
 */
export function resolveDropStartAt(raw: any): string | null {
  return readDateField(raw, ["start_time", "publish_at", "published_at", "start_at", "started_at", "start_date", "startDate", "mint_start"]);
}

/**
 * The drop's optional end time. `expire_time` comes FIRST: it is the field the
 * `/drop/{slug}` payload carries, and the one manual expiry fills
 * (`drop/expire_at/{id}` takes `expired_at`, the drop reads back `expire_time`).
 * An expired drop and a drop that reached its scheduled end are the same state,
 * and reading a stale `end_at` ahead of it would keep offering mint after expiry.
 */
export function resolveDropEndAt(raw: any): string | null {
  return readDateField(raw, ["expire_time", "expired_at", "expire_at", "end_at", "ended_at", "end_date", "endDate", "mint_end"]);
}

/** Maps a raw drop payload (whatever shape the manage API returns) to the app's Drop type. */
/** The profile's links object, as name → URL, keeping only real http(s) strings. */
function creatorLinks(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string" && /^https?:\/\//i.test(v.trim())) out[k] = v.trim();
  }
  return Object.keys(out).length ? out : undefined;
}

export function normalizeDrop(raw: any, lang: string, now: number = Date.now()): Drop {
  const cover = sanitizeDropUrl(raw?.logo || raw?.coverImage || raw?.cover_image || raw?.image || raw?.cover);
  const banner = sanitizeDropUrl(raw?.banner || raw?.bannerImage || raw?.banner_image || raw?.cover, cover);
  const creatorRaw = raw?.creator || raw?.artist || raw?.user || {};
  const twitterUser = raw?.meta_data?.meta?.twitter_username;

  const rawStatus = toDropRawStatus(raw?.status);
  const startAt = resolveDropStartAt(raw);
  const endAt = resolveDropEndAt(raw);

  // The single drop now embeds its items as one PAGE ({ data, page }); older payloads sent a bare array.
  const rawItems: any[] = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.items?.data)
      ? raw.items.data
      : Array.isArray(raw?.drop_items)
        ? raw.drop_items
        : [];

  return {
    id: String(raw?.slug ?? raw?.id ?? raw?.uuid ?? Date.now()),
    numericId: resolveDropNumericId(raw),
    slug: raw?.slug ?? raw?.id ?? raw?.uuid ?? undefined,
    // What like/drop/{id} and watchlist/drop/{id} get addressed by. The slug
    // fallback dates from when the drops endpoints returned no numeric `id` at
    // all: passing numericId alone left this undefined and the toggle hooks
    // bailed out before ever calling the API, which is why liking/watchlisting
    // a drop silently did nothing. Now that `id` is returned, the numeric
    // branch wins — matching every other model, which the like/watchlist routes
    // are all addressed by numeric id (see features/interactions/models.ts).
    // The slug fallback stays for payloads that still omit `id`.
    likeId: raw?.id != null && !Number.isNaN(Number(raw.id))
      ? Number(raw.id)
      : (raw?.slug ?? raw?.uuid ?? undefined),
    items: rawItems,
    itemsPage: resolveItemsPage(raw?.items?.page),
    itemsCount: typeof raw?.items_count === "number" && raw.items_count >= 0 ? raw.items_count : null,
    summary: resolveDropSummary(raw?.summary),
    title: (lang === "fa" ? raw?.meta_data?.meta?.fa_name : undefined) || raw?.name || raw?.title || raw?.collection_name || "—",
    creator: {
      id: creatorRaw?.id,
      name: creatorRaw?.name || creatorRaw?.username || "—",
      username: creatorRaw?.username,
      avatar: sanitizeDropUrl(creatorRaw?.profile?.cover || creatorRaw?.avatar || creatorRaw?.image || creatorRaw?.profile?.avatar),
      // The user payload's own banner (UserProfile.banner), not the drop's
      // banner — those are different images on different models. Empty-string
      // fallback (instead of the usual placeholder) so callers can tell
      // "no banner" apart from "banner is the placeholder" and fall back to
      // the avatar/cover themselves. sanitizeDropUrl trims, so a
      // whitespace-only value lands here as "" too.
      banner: sanitizeDropUrl(creatorRaw?.profile?.banner, ""),
      verified: Boolean(creatorRaw?.verified || creatorRaw?.blue_check || creatorRaw?.is_verified || raw?.blue_check),
      bio: creatorRaw?.profile?.bio || creatorRaw?.bio || undefined,
      // The profile's one-line tagline ("The collector") — shown when there is no bio.
      detail: creatorRaw?.profile?.detail || undefined,
      // The creator's own links ({ instagram: "https://…" }); only string URLs survive.
      links: creatorLinks(creatorRaw?.profile?.links),
      followers: Number(creatorRaw?.following?.follower ?? creatorRaw?.followers ?? 0),
      following: Number(creatorRaw?.following?.following ?? creatorRaw?.following_count ?? 0),
    },
    coverImage: cover,
    bannerImage: banner,
    description: raw?.description || raw?.detail || "",
    // Derived from the raw status AND the times — see permissions.getDropPhase.
    // It used to be a fuzzy substring match on the status alone, which reported
    // an accepted-but-not-yet-started drop as live and mintable.
    status: getDropPhase({ status: rawStatus, startAt, endAt }, now),
    rawStatus,
    startAt,
    endAt,
    // When the drop went public — the payload states it; nothing was reading it.
    publishedAt: typeof raw?.published_at === "string" && raw.published_at.trim() ? raw.published_at.trim() : null,
    // The final lock: set by the backend the moment the drop becomes a collection on chain. Never sent by the client.
    confirmationAt: typeof raw?.confirmation_at === "string" && raw.confirmation_at.trim() ? raw.confirmation_at.trim() : null,
    price: resolveDropPrice(raw),
    currency: raw?.currency || raw?.currency_symbol || raw?.symbol || "ETH",
    totalSupply: Number(raw?.totalSupply ?? raw?.total_supply ?? raw?.supply ?? raw?.items_count ?? 0),
    // No `?? 0`: a missing minted count is UNKNOWN, not zero, and the previous
    // coercion is what made every drop render 0 minted at 0% regardless of its
    // real state (F97). Null propagates to an unavailable state instead.
    mintedCount: resolveMintedCount(raw),
    likes: raw?.likes,
    current_user: raw?.current_user,
    meta_data: raw?.meta_data,
    discordUrl: raw?.meta_data?.meta?.discord_url || raw?.discordUrl || raw?.discord_url || undefined,
    twitterUrl: twitterUser ? `https://twitter.com/${twitterUser}` : (raw?.twitterUrl || raw?.twitter_url || undefined),
    websiteUrl: raw?.meta_data?.meta?.project_url || raw?.websiteUrl || raw?.website_url || undefined,
    telegramUrl: raw?.meta_data?.meta?.telegram_url || raw?.telegramUrl || raw?.telegram_url || undefined,
    instagramUrl: raw?.meta_data?.meta?.instagram_username ? `https://instagram.com/${raw.meta_data.meta.instagram_username}` : undefined,
    chain: (typeof raw?.chain === "string" ? raw.chain : raw?.network_name || raw?.network || "ETH").toString(),
  };
}
